"use client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form } from "@/components/ui/form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { X, Plus } from "lucide-react"

const formSchema = z.object({
  requirements: z.array(z.string()),
  skills: z.array(z.string()),
  techStacks: z.array(
    z.object({
      name: z.string(),
      level: z.enum(["junior", "mid", "senior", "lead"]),
      yearsOfExperience: z.coerce.number().int().min(0),
    }),
  ),
  languages: z.array(
    z.object({
      name: z.string(),
      proficiency: z.enum(["basic", "intermediate", "advanced", "fluent", "native"]),
    }),
  ),
  experience: z.string().optional(),
  education: z.string().optional(),
  keywords: z.array(z.string()),
})

export function RequirementsForm({ formData, updateFormData, onNext, onPrevious }) {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      requirements: formData.requirements.length ? formData.requirements : [""],
      skills: formData.skills.length ? formData.skills : [""],
      techStacks: formData.techStacks.length ? formData.techStacks : [{ name: "", level: "mid", yearsOfExperience: 1 }],
      languages: formData.languages.length ? formData.languages : [{ name: "", proficiency: "intermediate" }],
      experience: formData.experience || "",
      education: formData.education || "",
      keywords: formData.keywords.length ? formData.keywords : [""],
    },
  })

  const onSubmit = (data) => {
    // Filter out empty strings from arrays
    const filteredData = {
      ...data,
      requirements: data.requirements.filter((req) => req.trim() !== ""),
      skills: data.skills.filter((skill) => skill.trim() !== ""),
      keywords: data.keywords.filter((keyword) => keyword.trim() !== ""),
      techStacks: data.techStacks.filter((tech) => tech.name.trim() !== ""),
      languages: data.languages.filter((lang) => lang.name.trim() !== ""),
    }

    updateFormData(filteredData)
    onNext()
  }

  // Dynamic field arrays
  const addRequirement = () => {
    const currentRequirements = form.getValues("requirements")
    form.setValue("requirements", [...currentRequirements, ""])
  }

  const removeRequirement = (index) => {
    const currentRequirements = form.getValues("requirements")
    if (currentRequirements.length > 1) {
      form.setValue(
        "requirements",
        currentRequirements.filter((_, i) => i !== index),
      )
    }
  }

  const addSkill = () => {
    const currentSkills = form.getValues("skills")
    form.setValue("skills", [...currentSkills, ""])
  }

  const removeSkill = (index) => {
    const currentSkills = form.getValues("skills")
    if (currentSkills.length > 1) {
      form.setValue(
        "skills",
        currentSkills.filter((_, i) => i !== index),
      )
    }
  }

  const addTechStack = () => {
    const currentTechStacks = form.getValues("techStacks")
    form.setValue("techStacks", [...currentTechStacks, { name: "", level: "mid", yearsOfExperience: 1 }])
  }

  const removeTechStack = (index) => {
    const currentTechStacks = form.getValues("techStacks")
    if (currentTechStacks.length > 1) {
      form.setValue(
        "techStacks",
        currentTechStacks.filter((_, i) => i !== index),
      )
    }
  }

  const addLanguage = () => {
    const currentLanguages = form.getValues("languages")
    form.setValue("languages", [...currentLanguages, { name: "", proficiency: "intermediate" }])
  }

  const removeLanguage = (index) => {
    const currentLanguages = form.getValues("languages")
    if (currentLanguages.length > 1) {
      form.setValue(
        "languages",
        currentLanguages.filter((_, i) => i !== index),
      )
    }
  }

  const addKeyword = () => {
    const currentKeywords = form.getValues("keywords")
    form.setValue("keywords", [...currentKeywords, ""])
  }

  const removeKeyword = (index) => {
    const currentKeywords = form.getValues("keywords")
    if (currentKeywords.length > 1) {
      form.setValue(
        "keywords",
        currentKeywords.filter((_, i) => i !== index),
      )
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Requirements */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Requirements</Label>
            <Button type="button" variant="outline" size="sm" onClick={addRequirement}>
              <Plus className="h-4 w-4 mr-1" /> Add Requirement
            </Button>
          </div>

          {form.watch("requirements").map((_, index) => (
            <div key={`requirement-${index}`} className="flex items-center gap-2">
              <Input
                {...form.register(`requirements.${index}`)}
                placeholder="e.g., 5+ years of experience with React"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeRequirement(index)}
                disabled={form.watch("requirements").length <= 1}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Skills */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Skills</Label>
            <Button type="button" variant="outline" size="sm" onClick={addSkill}>
              <Plus className="h-4 w-4 mr-1" /> Add Skill
            </Button>
          </div>

          {form.watch("skills").map((_, index) => (
            <div key={`skill-${index}`} className="flex items-center gap-2">
              <Input {...form.register(`skills.${index}`)} placeholder="e.g., JavaScript" />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeSkill(index)}
                disabled={form.watch("skills").length <= 1}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Tech Stacks */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Tech Stacks</Label>
            <Button type="button" variant="outline" size="sm" onClick={addTechStack}>
              <Plus className="h-4 w-4 mr-1" /> Add Tech Stack
            </Button>
          </div>

          {form.watch("techStacks").map((_, index) => (
            <div
              key={`techStack-${index}`}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-3 border rounded-md"
            >
              <div className="w-full sm:w-1/3">
                <Input {...form.register(`techStacks.${index}.name`)} placeholder="e.g., React" />
              </div>
              <div className="w-full sm:w-1/3">
                <Select
                  onValueChange={(value) => form.setValue(`techStacks.${index}.level`, value)}
                  defaultValue={form.watch(`techStacks.${index}.level`)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="junior">Junior</SelectItem>
                    <SelectItem value="mid">Mid</SelectItem>
                    <SelectItem value="senior">Senior</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-1/4">
                <Input
                  type="number"
                  min="0"
                  {...form.register(`techStacks.${index}.yearsOfExperience`, { valueAsNumber: true })}
                  placeholder="Years"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeTechStack(index)}
                disabled={form.watch("techStacks").length <= 1}
                className="mt-2 sm:mt-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Languages */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Languages</Label>
            <Button type="button" variant="outline" size="sm" onClick={addLanguage}>
              <Plus className="h-4 w-4 mr-1" /> Add Language
            </Button>
          </div>

          {form.watch("languages").map((_, index) => (
            <div key={`language-${index}`} className="flex items-center gap-2">
              <div className="w-1/2">
                <Input {...form.register(`languages.${index}.name`)} placeholder="e.g., English" />
              </div>
              <div className="w-1/2">
                <Select
                  onValueChange={(value) => form.setValue(`languages.${index}.proficiency`, value)}
                  defaultValue={form.watch(`languages.${index}.proficiency`)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select proficiency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="fluent">Fluent</SelectItem>
                    <SelectItem value="native">Native</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeLanguage(index)}
                disabled={form.watch("languages").length <= 1}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Experience */}
        <div className="space-y-2">
          <Label htmlFor="experience">Experience</Label>
          <Textarea id="experience" {...form.register("experience")} placeholder="Describe the required experience" />
        </div>

        {/* Education */}
        <div className="space-y-2">
          <Label htmlFor="education">Education</Label>
          <Textarea id="education" {...form.register("education")} placeholder="Describe the required education" />
        </div>

        {/* Keywords */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Keywords</Label>
            <Button type="button" variant="outline" size="sm" onClick={addKeyword}>
              <Plus className="h-4 w-4 mr-1" /> Add Keyword
            </Button>
          </div>

          {form.watch("keywords").map((_, index) => (
            <div key={`keyword-${index}`} className="flex items-center gap-2">
              <Input {...form.register(`keywords.${index}`)} placeholder="e.g., frontend" />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeKeyword(index)}
                disabled={form.watch("keywords").length <= 1}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onPrevious}>
            Previous
          </Button>
          <Button type="submit">Next</Button>
        </div>
      </form>
    </Form>
  )
}

